const SKIP_KEY = /(password|passhash|pin_hash|token|secret|ciphertext|otp|html)\b/i;
const MAX_LEN = 8_000;

/** Strip C0 controls that can break Postgres text and logs. Keep tab/newline. */
export function stripNullsAndControls(value: string): string {
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
}

/**
 * User-typed text stored in Postgres. Parameterized queries already block SQL
 * injection; this stops stored markup / script from surviving into notes, names,
 * and other free-text columns. Leaves `qty < 3` style comparisons alone.
 */
export function sanitizeUserText(value: string, maxLen = MAX_LEN): string {
  let cur = stripNullsAndControls(value);
  if (cur.length > maxLen) cur = cur.slice(0, maxLen);
  let prev = '';
  while (cur !== prev) {
    prev = cur;
    cur = cur.replace(/<!--[\s\S]*?-->/g, '');
    cur = cur.replace(/<\/?[a-zA-Z][^>]*>/g, '');
  }
  return cur;
}

export function sanitizeWritePayload(value: unknown, key?: string): unknown {
  if (typeof value === 'string') {
    if (key && SKIP_KEY.test(key)) return stripNullsAndControls(value);
    return sanitizeUserText(value);
  }
  if (Array.isArray(value)) return value.map((item) => sanitizeWritePayload(item, key));
  if (value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = sanitizeWritePayload(v, k);
    }
    return out;
  }
  return value;
}

/** Wrap insert/update/upsert so every table write is cleaned. */
export function attachWriteSanitizer<T extends { from: (table: string) => unknown }>(client: T): T {
  const originalFrom = client.from.bind(client);
  client.from = ((table: string) => {
    const builder = originalFrom(table) as {
      insert: (...args: unknown[]) => unknown;
      update: (...args: unknown[]) => unknown;
      upsert: (...args: unknown[]) => unknown;
    };
    const originalInsert = builder.insert.bind(builder);
    const originalUpdate = builder.update.bind(builder);
    const originalUpsert = builder.upsert.bind(builder);
    builder.insert = (values: unknown, options?: unknown) =>
      originalInsert(sanitizeWritePayload(values), options);
    builder.update = (values: unknown, options?: unknown) =>
      originalUpdate(sanitizeWritePayload(values), options);
    builder.upsert = (values: unknown, options?: unknown) =>
      originalUpsert(sanitizeWritePayload(values), options);
    return builder;
  }) as T['from'];
  return client;
}
